import React from "react";
import { useTranslation } from "../../../../../explorer/hooks/useTranslation";
import {
    AccordionSummary,
    AccordionDetails,
    TableContainer,
    Paper,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Tooltip,
    Box,
} from "@mui/material";
import { BusinessUnit } from "../../../../../explorer/types/SpareParts/BusinessUnit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import useGoToPage from "../../../../hook/useGoToPage";
import {
    AccordionStyled,
    SectionIcon,
    TitleTextAccordion,
    useClasses,
} from "./InstalledBaseAccordion.styles";
import InstalledBaseAccordionStatus from "./InstalledBaseAccordionStatus";
import GoToPage from "../../../../components/GoToPage/GoToPage";
import { useReduxState } from "../../../../../explorer/redux/hooks";
import { Machine } from "../../../../../explorer/types/SpareParts";
import { reduxStoreConfig } from "../../../../configuration/reduxStoreConfig";
import {
    AppIconsCamera,
    AppIconsDocument,
    AppIconsEmergency,
    AppIconsInfo,
    AppIconsMaintenance,
} from "../../../../components/AppIcons";
import RenderBusinessUnitIcon from "./InstalledBaseAccordionIcon";
import ImageTooltip from "../../../../components/AppTooltip/ImageTooltip";
import { MACHINES_RESOURCES } from "../../../../configuration/Resources";
import useLanguages from "../../../../../explorer/hooks/useLanguages";

type TInstalledBaseAccordionProps = {
    businessUnit: BusinessUnit;
    expandedAll: boolean;
    idx: number;
};

const InstalledBaseAccordion = (props: TInstalledBaseAccordionProps) => {
    const { t } = useTranslation();
    const classes = useClasses();
    const expandedAll = props.expandedAll;
    const idx = props.idx;
    const goToPage = useGoToPage();
    const lang = useLanguages().currentLanguage;

    const [expanded, setExpanded] = React.useState<boolean>(expandedAll);
    const [currentMachine, setCurrentMachine] = useReduxState<Machine>(
        reduxStoreConfig.sparePartsCurrentMachine
    );

    const handleExpansion = () => {
        setExpanded(!expanded);
    };

    React.useEffect(() => {
        setExpanded(expandedAll);
    }, [expandedAll]);

    React.useEffect(() => {
        setExpanded(idx === 0);
    }, []);

    // Da non cancellare nel caso che si decida di usare nuovamente il formato della data in base alla linga
    // const formatDateByLocale = (date: Date, locale: string): string => {
    //     return date.toLocaleDateString(locale, {
    //         day: "2-digit",
    //         month: "2-digit",
    //         year: "numeric",
    //     });
    // };

    return (
        <AccordionStyled expanded={expanded} onChange={handleExpansion}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${props.businessUnit.name}-content`}
                id={`panel${props.businessUnit.name}`}
            >
                <SectionIcon>
                    <RenderBusinessUnitIcon status={props.businessUnit.name} />
                </SectionIcon>

                <TitleTextAccordion>
                    {props.businessUnit.name}
                </TitleTextAccordion>
            </AccordionSummary>

            <AccordionDetails>
                <TableContainer component={Paper}>
                    <Table size="small" aria-label="simple table">
                        <TableBody sx={{ flexGrow: 1 }}>
                            {props.businessUnit.machines.map((machine) => (
                                <TableRow
                                    key={machine.id}
                                    sx={classes.tableRow}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();

                                        setCurrentMachine(machine);

                                        goToPage.spareParts.goToMachine(
                                            machine.customerSiteCode!,
                                            machine.id!.toString()
                                        );
                                    }}
                                >
                                    <TableCell
                                        className="icons"
                                        sx={classes.iconCell}
                                        width={50}
                                        align="center"
                                    >
                                        <InstalledBaseAccordionStatus
                                            status={machine.status!}
                                        />
                                    </TableCell>

                                    <TableCell width={50} align="left">
                                        {machine.serialNumber}
                                    </TableCell>
                                    <TableCell width={50} align="left">
                                        {machine.assetRecordType}
                                    </TableCell>

                                    <TableCell width={50} align="center">
                                        {machine.hasImage &&
                                            machine.modelImage && (
                                                <ImageTooltip
                                                    imageStyle={{
                                                        maxWidth: 150,
                                                    }}
                                                    imagePath={`${MACHINES_RESOURCES}/${machine.modelImage
                                                        .replace("\\", "/")
                                                        .replace(
                                                            ".jpg",
                                                            ".thumb.300.jpg"
                                                        )}`}
                                                >
                                                    <AppIconsCamera
                                                        style={{ marginTop: 4 }}
                                                        sx={classes.iconSize}
                                                    />
                                                </ImageTooltip>
                                            )}
                                    </TableCell>

                                    <TableCell align="left">
                                        {machine.description}
                                    </TableCell>

                                    <TableCell
                                        sx={classes.iconCell}
                                        width={200}
                                        align="center"
                                    >
                                        <Tooltip
                                            placement="top"
                                            title={
                                                <React.Fragment>
                                                    <Box>
                                                        <Box
                                                            sx={classes.tooltip}
                                                        >
                                                            {t(
                                                                "common.spareparts.installationDate"
                                                            )}
                                                            {/* <b>
                                                                {machine.installationDate !==
                                                                    null &&
                                                                    machine.installationDate ?
                                                                    formatDateByLocale(
                                                                        new Date(
                                                                            machine.installationDate
                                                                        ),
                                                                        lang
                                                                    ) : 
                                                                    (
                                                                        t("common.spareparts.na")
                                                                    )}
                                                            </b> */}
                                                            <b>
                                                                {machine.installationDate !==
                                                                    null &&
                                                                machine.installationDate
                                                                    ? (() => {
                                                                          const date =
                                                                              new Date(
                                                                                  machine.installationDate
                                                                              );
                                                                          const day =
                                                                              date
                                                                                  .getDate()
                                                                                  .toString()
                                                                                  .padStart(
                                                                                      2,
                                                                                      "0"
                                                                                  );
                                                                          const month =
                                                                              (
                                                                                  date.getMonth() +
                                                                                  1
                                                                              )
                                                                                  .toString()
                                                                                  .padStart(
                                                                                      2,
                                                                                      "0"
                                                                                  ); // Mese da 0 a 11, quindi +1
                                                                          const year =
                                                                              date.getFullYear();
                                                                          return `${day}-${month}-${year}`;
                                                                      })()
                                                                    : t(
                                                                          "common.spareparts.na"
                                                                      )}
                                                            </b>
                                                        </Box>
                                                        <Box
                                                            sx={classes.tooltip}
                                                        >
                                                            {machine.warrantyPeriod !==
                                                                null &&
                                                                t(
                                                                    "common.spareparts.warrantyPeriod"
                                                                )}
                                                            <b>
                                                                {machine.warrantyPeriod !==
                                                                    null &&
                                                                    machine.warrantyPeriod &&
                                                                    machine.warrantyPeriod}
                                                            </b>
                                                        </Box>
                                                    </Box>
                                                </React.Fragment>
                                            }
                                        >
                                            <AppIconsInfo
                                                sx={classes.iconSize}
                                            />
                                        </Tooltip>
                                    </TableCell>

                                    <TableCell
                                        sx={classes.iconCell}
                                        width={40}
                                        align="center"
                                    >
                                        {machine.hasDocument && (
                                            <GoToPage
                                                tooltipTitle={t(
                                                    "common.spareparts.thecnicalDoc"
                                                )}
                                                params={{
                                                    customerSiteCode:
                                                        machine.customerSiteCode!,
                                                    machineId:
                                                        machine.id!.toString(),
                                                    tabIndex:
                                                        "TechnicalDocumentation",
                                                }}
                                                page={"spareparts.machineMore"}
                                                onClick={(event: any) => {
                                                    setCurrentMachine(machine);
                                                    event.stopPropagation();
                                                }}
                                                children={
                                                    <AppIconsDocument
                                                        sx={classes.iconSize}
                                                    />
                                                }
                                            />
                                        )}
                                    </TableCell>

                                    <TableCell
                                        sx={classes.iconCell}
                                        width={40}
                                        align="center"
                                    >
                                        {machine.hasMaintList && (
                                            <GoToPage
                                                tooltipTitle={t(
                                                    "common.spareparts.maintList"
                                                )}
                                                params={{
                                                    customerSiteCode:
                                                        machine.customerSiteCode!,
                                                    machineId:
                                                        machine.id!.toString(),
                                                    tabIndex: "MaintenanceList",
                                                }}
                                                page={"spareparts.machineMore"}
                                                onClick={(event: any) => {
                                                    setCurrentMachine(machine);
                                                    event.stopPropagation();
                                                }}
                                                children={
                                                    <AppIconsMaintenance
                                                        sx={classes.iconSize}
                                                    />
                                                }
                                            />
                                        )}
                                    </TableCell>

                                    <TableCell
                                        sx={classes.iconCell}
                                        width={40}
                                        align="center"
                                    >
                                        {machine.hasEmergList && (
                                            <GoToPage
                                                tooltipTitle={t(
                                                    "common.spareparts.emergList"
                                                )}
                                                params={{
                                                    customerSiteCode:
                                                        machine.customerSiteCode!,
                                                    machineId:
                                                        machine.id!.toString(),
                                                    tabIndex: "EmergencyList",
                                                }}
                                                page={"spareparts.machineMore"}
                                                onClick={(event: any) => {
                                                    setCurrentMachine(machine);
                                                    event.stopPropagation();
                                                }}
                                                children={
                                                    <AppIconsEmergency
                                                        sx={classes.iconSize}
                                                    />
                                                }
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </AccordionDetails>
        </AccordionStyled>
    );
};

export default InstalledBaseAccordion;
